/**
 * "New case" (#758): start a case from Patchbay instead of waiting for a Desk ticket. One dialog, minimal fields, the
 * rest pre-filled (#746): customer, contact, how it started, subject, the first message or note, next move and owner.
 * "Review" freezes a `create_case` card; nothing exists in Desk and nothing is emailed until the person confirms it,
 * and the customer gets an email only when "Send it now" is ticked (off by default).
 */
import { Button, Chip, ConfirmCard, Dialog, DialogClose, ErrorBanner, Input, Select, Textarea, toast } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useRouter } from '@tanstack/react-router'
import * as React from 'react'
import { ApiError, describeError } from '../../lib/api/client'
import { goToCase } from '../../lib/go-to'
import { useMe } from '../auth/session'
import { OwnerSelect } from '../case-owner/case-owner'
import {
  type CardOut,
  CHANNELS,
  type Channel,
  confirmNewCaseCard,
  contactsQuery,
  customersQuery,
  defaultNextMove,
  dismissNewCaseCard,
  dueInDays,
  type NewCaseIn,
  ownersQuery,
  proposeNewCase,
} from './api'

export interface NewCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Prefill from an account or contact page. */
  preset?: { accountId?: string; accountName?: string; contactEmail?: string; contactName?: string }
  onCreated?: (out: { caseNumber: string }) => void
}

export function NewCaseDialog({ open, onOpenChange, ...rest }: NewCaseDialogProps) {
  // #1233: the form reports whether anything typed would be lost; a closed dialog has nothing to lose.
  const [dirty, setDirty] = React.useState(false)
  React.useEffect(() => {
    if (!open) setDirty(false)
  }, [open])
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      dirty={dirty}
      title="New case"
      description="Starts a Desk ticket and a Patchbay case. Nothing is emailed to the customer unless you choose Send it now."
    >
      {/* Mounted only while open, so every opening starts clean. */}
      {open ? <NewCaseForm {...rest} onDirtyChange={setDirty} onClose={() => onOpenChange(false)} /> : null}
    </Dialog>
  )
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value)
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

type Stage = { kind: 'form' } | { kind: 'proposing' } | { kind: 'card'; card: CardOut } | { kind: 'running'; card: CardOut }

interface Customer {
  name: string
  accountId: string | null
  conduitCustomerId: string | null
}

function NewCaseForm({
  preset,
  onCreated,
  onClose,
  onDirtyChange,
}: Omit<NewCaseDialogProps, 'open' | 'onOpenChange'> & { onClose: () => void; onDirtyChange: (dirty: boolean) => void }) {
  const router = useRouter()
  const qc = useQueryClient()
  const me = useMe()
  const [requestId] = React.useState(() => crypto.randomUUID())
  const [search, setSearch] = React.useState('')
  const [customer, setCustomer] = React.useState<Customer | null>(
    preset?.accountId || preset?.accountName
      ? { name: preset.accountName ?? `Account ${preset.accountId}`, accountId: preset.accountId ?? null, conduitCustomerId: null }
      : null,
  )
  const [contactEmail, setContactEmail] = React.useState(preset?.contactEmail ?? '')
  const [contactName, setContactName] = React.useState(preset?.contactName ?? '')
  const [contactId, setContactId] = React.useState<string | null>(null)
  const [channel, setChannel] = React.useState<Channel>('email_out')
  const [subject, setSubject] = React.useState('')
  const [text, setText] = React.useState('')
  const [sendNow, setSendNow] = React.useState(false)
  const [moveText, setMoveText] = React.useState(defaultNextMove('email_out', false, preset?.contactName ?? '').text)
  const [moveOwner, setMoveOwner] = React.useState<'us' | 'customer'>('us')
  const [moveDue, setMoveDue] = React.useState(dueInDays(1))
  const [ownerAgent, setOwnerAgent] = React.useState('')
  const [touched, setTouched] = React.useState({ move: false, owner: false })
  const [stage, setStage] = React.useState<Stage>({ kind: 'form' })
  const [error, setError] = React.useState<unknown>(null)

  // #1233: dirty = a field differs from how it opened. Defaults the form fills itself (contact, owner, next move) are not.
  const presetCustomer = Boolean(preset?.accountId || preset?.accountName)
  const dirty =
    search.trim() !== '' ||
    subject.trim() !== '' ||
    text.trim() !== '' ||
    sendNow ||
    touched.move ||
    touched.owner ||
    channel !== 'email_out' ||
    (customer !== null) !== presetCustomer ||
    (preset?.contactEmail !== undefined && contactEmail !== preset.contactEmail)
  React.useEffect(() => {
    onDirtyChange(dirty)
  }, [dirty, onDirtyChange])

  const term = useDebounced(search.trim(), 250)
  const customers = useQuery({ ...customersQuery(term), enabled: !customer && term.length >= 2 })
  const contacts = useQuery(contactsQuery(customer?.accountId ?? null, '', Boolean(customer?.accountId)))
  const people = useQuery(ownersQuery())

  // Default owner: the person starting the case, once the staff list is in.
  React.useEffect(() => {
    if (touched.owner || !people.data || !me) return
    const mine = people.data.people.find((p) => p.user_id === me.id)
    if (mine) setOwnerAgent(mine.desk_agent_id)
  }, [people.data, me, touched.owner])

  // The first contact on the account is the default; typing an address overrides it.
  const firstContact = contacts.data?.contacts[0]
  React.useEffect(() => {
    if (contactEmail || !firstContact) return
    setContactEmail(firstContact.email)
    setContactName(firstContact.name ?? '')
    setContactId(firstContact.contact_id ?? null)
  }, [firstContact, contactEmail])

  // The next move follows how the case started until the person edits it.
  const effectiveSend = channel === 'email_out' && sendNow
  React.useEffect(() => {
    if (touched.move) return
    const next = defaultNextMove(channel, effectiveSend, contactName)
    setMoveText(next.text)
    setMoveOwner(next.owner)
    setMoveDue(dueInDays(next.owner === 'customer' ? 2 : 1))
  }, [channel, effectiveSend, contactName, touched.move])

  const ready = Boolean(customer) && contactEmail.includes('@') && subject.trim().length > 0 && (!effectiveSend || text.trim().length > 0)
  const body: NewCaseIn | null = ready
    ? {
        request_id: requestId,
        account_id: customer?.accountId ?? null,
        account_name: customer?.name ?? null,
        conduit_customer_id: customer?.conduitCustomerId ?? null,
        contact_id: contactId,
        contact_email: contactEmail.trim(),
        contact_name: contactName.trim() || null,
        channel,
        subject: subject.trim(),
        body: text.trim() || null,
        send_now: effectiveSend,
        next_move_text: moveText.trim() || null,
        next_move_owner: moveOwner,
        next_move_due: moveText.trim() ? moveDue || null : null,
        owner_desk_agent_id: ownerAgent || null,
      }
    : null

  const review = async () => {
    if (!body) return
    setError(null)
    setStage({ kind: 'proposing' })
    try {
      setStage({ kind: 'card', card: await proposeNewCase(body) })
    } catch (err) {
      setError(err)
      setStage({ kind: 'form' })
    }
  }

  const backToForm = async (card: CardOut, reason: string | null = 'edited before confirming') => {
    setStage({ kind: 'form' })
    setError(null)
    try {
      await dismissNewCaseCard(card.id, reason)
    } catch {
      // A card left proposed expires on its own; it never ran.
    }
  }

  const confirmNow = async (card: CardOut) => {
    setError(null)
    setStage({ kind: 'running', card })
    try {
      const out = await confirmNewCaseCard(card.id)
      const result = (out.result ?? {}) as { case_number?: string; emailed_customer?: boolean; warnings?: string[] }
      await qc.invalidateQueries()
      toast(`Case ${result.case_number ?? ''} created${result.emailed_customer ? ' and the customer was emailed' : ''}`)
      for (const w of result.warnings ?? []) toast(w)
      if (result.case_number) {
        onCreated?.({ caseNumber: result.case_number })
        onClose()
        void goToCase(router, result.case_number)
      } else {
        onClose()
      }
    } catch (err) {
      setError(err)
      setStage({ kind: 'card', card })
    }
  }

  if (stage.kind === 'card' || stage.kind === 'running') {
    const card = stage.card
    const preview = (card.preview ?? {}) as Record<string, string | boolean | null | undefined>
    return (
      <div className="space-y-3">
        <ConfirmCard
          kind={card.kind}
          title={card.title ?? 'New case'}
          impact={{ audience: card.audience === 'customer' ? 'customer' : 'internal', text: card.impact_text }}
          executor={card.executor}
          status={stage.kind === 'running' ? 'running' : 'pending'}
          onConfirm={() => void confirmNow(card)}
          onEdit={() => void backToForm(card)}
          onDismiss={(reason) => void backToForm(card, reason ?? null)}
        >
          <dl className="grid gap-x-3 gap-y-1 sm:grid-cols-[90px_1fr]">
            <dt className="text-muted-foreground">Customer</dt>
            <dd>{preview.customer}</dd>
            <dt className="text-muted-foreground">Contact</dt>
            <dd>{preview.contact}</dd>
            <dt className="text-muted-foreground">Started</dt>
            <dd>{preview.channel}</dd>
            <dt className="text-muted-foreground">Subject</dt>
            <dd data-slot="card-subject">{preview.subject}</dd>
            {preview.owner ? (
              <>
                <dt className="text-muted-foreground">Owner</dt>
                <dd>{preview.owner}</dd>
              </>
            ) : null}
            {preview.next_move ? (
              <>
                <dt className="text-muted-foreground">Next move</dt>
                <dd>{preview.next_move}</dd>
              </>
            ) : null}
          </dl>
          {preview.body ? (
            <p data-slot="card-body" className="mt-2 whitespace-pre-wrap rounded-md border border-border bg-muted/40 px-3 py-2">
              {preview.body}
            </p>
          ) : null}
        </ConfirmCard>
        {!card.can_confirm ? <p className="text-sm text-warning">Your role cannot confirm this card.</p> : null}
        <Problem error={error} />
      </div>
    )
  }

  const messageLabel = channel === 'email_out' ? 'First email' : channel === 'called' ? 'What they said' : 'What was asked'
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        void review()
      }}
    >
      <div className="space-y-1.5">
        <label className="block text-sm font-medium" htmlFor="nc-customer">
          Customer
        </label>
        {customer ? (
          <div className="flex items-center gap-2 text-sm">
            <Chip tone="outline">{customer.name}</Chip>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setCustomer(null)
                setContactEmail('')
                setContactName('')
                setContactId(null)
              }}
            >
              Change
            </Button>
          </div>
        ) : (
          <>
            <Input
              id="nc-customer"
              value={search}
              placeholder="Search customers (Desk and Conduit)"
              onChange={(e) => setSearch(e.target.value)}
            />
            {customers.isError ? (
              <ErrorBanner
                tone="warning"
                title="Customer search unreachable"
                detail={`${describeError(customers.error)}. This is not an empty result.`}
                onRetry={() => customers.refetch()}
              />
            ) : customers.data ? (
              <>
                {customers.data.conduit === 'unreachable' ? (
                  <p role="status" className="text-xs text-warning">
                    Conduit did not answer, so Conduit-only customers are not listed. This is not "none".
                  </p>
                ) : null}
                {customers.data.customers.length ? (
                  <ul aria-label="Customers" className="max-h-44 divide-y divide-border overflow-y-auto rounded-md border border-border">
                    {customers.data.customers.map((c) => (
                      <li key={`${c.account_id ?? ''}${c.conduit_customer_id ?? ''}${c.name}`}>
                        <button
                          type="button"
                          onClick={() =>
                            setCustomer({ name: c.name, accountId: c.account_id ?? null, conduitCustomerId: c.conduit_customer_id ?? null })
                          }
                          className="flex w-full cursor-pointer items-baseline gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted"
                        >
                          <span className="font-medium">{c.name}</span>
                          <span className="text-xs text-subtle">{[c.detail, c.sources.join(' + ')].filter(Boolean).join(' · ')}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">Nothing matched "{term}".</p>
                )}
              </>
            ) : customers.isFetching ? (
              <p role="status" className="text-sm text-muted-foreground">
                Searching…
              </p>
            ) : null}
          </>
        )}
      </div>

      {customer ? (
        <div className="space-y-1.5">
          <span className="block text-sm font-medium">Contact</span>
          {contacts.data?.contacts.length ? (
            <div role="radiogroup" aria-label="Contacts" className="space-y-1">
              {contacts.data.contacts.map((c) => (
                <label key={c.email} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="nc-contact"
                    checked={contactEmail === c.email}
                    onChange={() => {
                      setContactEmail(c.email)
                      setContactName(c.name ?? '')
                      setContactId(c.contact_id ?? null)
                    }}
                  />
                  <span>
                    {c.name ? `${c.name} · ` : ''}
                    {c.email}
                  </span>
                </label>
              ))}
            </div>
          ) : contacts.isError ? (
            <p className="text-sm text-warning">Contacts could not be read ({describeError(contacts.error)}); type the address.</p>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              aria-label="Contact email"
              type="email"
              value={contactEmail}
              placeholder="name@customer.example"
              onChange={(e) => {
                setContactEmail(e.target.value)
                setContactId(null)
              }}
            />
            <Input aria-label="Contact name" value={contactName} placeholder="Name" onChange={(e) => setContactName(e.target.value)} />
          </div>
        </div>
      ) : null}

      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">How did it start?</legend>
        <div role="radiogroup" aria-label="Channel" className="flex flex-wrap gap-1.5">
          {CHANNELS.map((c) => (
            // biome-ignore lint/a11y/useSemanticElements: a segmented control; the buttons carry role=radio
            <button
              key={c.value}
              type="button"
              role="radio"
              aria-checked={channel === c.value}
              title={c.hint}
              onClick={() => setChannel(c.value)}
              className={`cursor-pointer rounded-md border px-2.5 py-1 text-sm ${channel === c.value ? 'border-primary bg-primary/10 font-medium' : 'border-border hover:bg-muted'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium" htmlFor="nc-subject">
          Subject
        </label>
        <Input
          id="nc-subject"
          maxLength={200}
          value={subject}
          placeholder="Reception phones drop calls"
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <label className="block text-sm font-medium" htmlFor="nc-text">
          {messageLabel}
        </label>
        <Textarea id="nc-text" rows={4} value={text} onChange={(e) => setText(e.target.value)} />
        {channel === 'email_out' ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendNow} onChange={(e) => setSendNow(e.currentTarget.checked)} />
            <span>Send it now (the customer is emailed when you confirm). Off keeps it as a private draft note.</span>
          </label>
        ) : (
          <p className="text-xs text-muted-foreground">Saved as a private note. The customer is not emailed.</p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <OwnerSelect
          label="Owner"
          value={ownerAgent}
          onChange={(v) => {
            setTouched((t) => ({ ...t, owner: true }))
            setOwnerAgent(v)
          }}
          people={people.data?.people}
          unreachable={people.data?.desk_directory === 'unreachable'}
        />
        <div className="space-y-1 text-xs font-medium">
          <label htmlFor="nc-move">Next move</label>
          <Input
            id="nc-move"
            value={moveText}
            onChange={(e) => {
              setTouched((t) => ({ ...t, move: true }))
              setMoveText(e.target.value)
            }}
          />
          <div className="flex items-center gap-2 font-normal">
            <Select
              aria-label="Next move owner"
              className="h-8 rounded-md border border-input bg-card px-2 text-sm"
              value={moveOwner}
              onChange={(e) => {
                setTouched((t) => ({ ...t, move: true }))
                setMoveOwner(e.target.value as 'us' | 'customer')
              }}
            >
              <option value="us">Us</option>
              <option value="customer">Waiting on customer</option>
            </Select>
            <Input aria-label="Next move due" type="date" value={moveDue} onChange={(e) => setMoveDue(e.target.value)} />
          </div>
        </div>
      </div>

      <Problem error={error} />
      <div className="flex justify-end gap-2">
        <DialogClose render={<Button type="button" variant="ghost" />}>Cancel</DialogClose>
        <Button type="submit" disabled={!body || stage.kind === 'proposing'}>
          {stage.kind === 'proposing' ? 'Preparing…' : 'Review'}
        </Button>
      </div>
    </form>
  )
}

function Problem({ error }: { error: unknown }) {
  if (!error) return null
  if (error instanceof ApiError && error.code === 'connect_desk') {
    return (
      <div role="alert" className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-sm">
        <span className="flex-1">Connect Desk first. The ticket is created with your own Desk token; nothing was created.</span>
        <Link to="/me" className="text-sm font-medium text-primary underline-offset-2 hover:underline">
          Connect Desk
        </Link>
      </div>
    )
  }
  if (error instanceof ApiError && error.code === 'new_case_unconfirmed') {
    return (
      <ErrorBanner
        title="Desk did not confirm the ticket"
        detail={`${describeError(error)} Dismiss this card and start again once you have checked.`}
      />
    )
  }
  return <ErrorBanner title="Could not create the case" detail={describeError(error)} />
}
